import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Users, Mail, Globe, Activity, Download, RefreshCw } from "lucide-react";
import { BACKEND_URL } from "../lib/backend";

// Nephrix Logo Component
const NephrixIcon = ({ className = "h-8 w-8" }) => (
  <img 
    src="/nephrix-logo.png" 
    alt="Nephrix" 
    className={`${className} object-contain`}
  />
);

const dialysisTypeLabels = {
  hemodialysis: "Hemodialysis",
  peritoneal: "Peritoneal Dialysis",
  ckd: "CKD (not on dialysis)"
};

const WaitlistAdmin = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const fetchEntries = async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("nephrix_admin_token");
      if (!token) {
        navigate("/admin/beta");
        return;
      }
      const response = await fetch(`${BACKEND_URL}/api/waitlist/admin`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.status === 401) {
        localStorage.removeItem("nephrix_admin_token");
        navigate("/admin/beta");
        return;
      }
      if (response.ok) {
        const data = await response.json();
        setEntries(data);
      } else {
        setError("Failed to fetch waitlist entries");
      }
    } catch (err) {
      setError("Failed to connect to server");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, []);

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const exportToCSV = () => {
    const headers = ["Email", "Dialysis Type", "Country", "Signup Date"];
    const rows = entries.map(e => [
      e.email,
      e.dialysis_type || "Not specified",
      e.country || "Not specified",
      formatDate(e.signup_timestamp)
    ]);
    
    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
    ].join("\n");
    
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `nephrix-waitlist-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  // Stats
  const stats = {
    total: entries.length,
    hemodialysis: entries.filter(e => e.dialysis_type === "hemodialysis").length,
    peritoneal: entries.filter(e => e.dialysis_type === "peritoneal").length,
    ckd: entries.filter(e => e.dialysis_type === "ckd").length,
    countries: [...new Set(entries.filter(e => e.country).map(e => e.country))].length
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <NephrixIcon className="h-10 w-10" />
          </Link>
          <Link 
            to="/" 
            className="flex items-center gap-2 text-slate-600 hover:text-blue-600 transition-colors text-sm font-medium"
            data-testid="back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-6xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Title */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 
                className="text-3xl font-bold text-slate-900 mb-2"
                style={{ fontFamily: "Manrope, sans-serif" }}
                data-testid="admin-title"
              >
                Waitlist Admin
              </h1>
              <p className="text-slate-600">Manage your beta waitlist signups</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={fetchEntries}
                className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
                data-testid="refresh-btn"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
              <button
                onClick={exportToCSV}
                disabled={entries.length === 0}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                data-testid="export-btn"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-xl p-4 border border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
                  <p className="text-xs text-slate-500">Total Signups</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <Activity className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.hemodialysis}</p>
                  <p className="text-xs text-slate-500">Hemodialysis</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Activity className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.peritoneal}</p>
                  <p className="text-xs text-slate-500">Peritoneal</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Activity className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.ckd}</p>
                  <p className="text-xs text-slate-500">CKD</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                  <Globe className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.countries}</p>
                  <p className="text-xs text-slate-500">Countries</p>
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
            {loading ? (
              <div className="p-12 text-center">
                <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-slate-500">Loading waitlist...</p>
              </div>
            ) : error ? (
              <div className="p-12 text-center">
                <p className="text-red-500">{error}</p>
                <button
                  onClick={fetchEntries}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Try Again
                </button>
              </div>
            ) : entries.length === 0 ? (
              <div className="p-12 text-center">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No waitlist signups yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="waitlist-table">
                  <thead className="bg-slate-50 border-b border-slate-100">
                    <tr>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">#</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Email</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Dialysis Type</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Country</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Signup Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {entries.map((entry, index) => (
                      <tr key={entry.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4 text-sm text-slate-400">{index + 1}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-slate-400" />
                            <span className="text-sm font-medium text-slate-900">{entry.email}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {entry.dialysis_type ? (
                            <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-50 text-blue-700">
                              {dialysisTypeLabels[entry.dialysis_type] || entry.dialysis_type}
                            </span>
                          ) : (
                            <span className="text-sm text-slate-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {entry.country ? (
                            <span className="text-sm text-slate-700">{entry.country}</span>
                          ) : (
                            <span className="text-sm text-slate-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-500">
                          {formatDate(entry.signup_timestamp)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default WaitlistAdmin;
